import {
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ScreenError,
  ScreenWrap,
  SearchInput,
  SelectInput,
  SoloLoading,
} from '../../components';
import ClientsRow from './ClientsRow';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { PROGRAM_STAGE_FILTER_OPTIONS } from '../../models/Program';
import { useTypedSelector } from '../../reducers';
import { iriToId } from '../../util';
import { useEffect } from 'react';
import { useInvalidation } from 'api-read-hook';
import { useAuthRoles } from '../../hooks';

export default function ClientsListScreen() {
  const { invalidateExact } = useInvalidation();
  const {
    getParams,
    setModifiedParams,
    tablePaginationProps,
    tableSortLabelProps,
  } = useModifiableParams();

  const params = getParams();

  const clinicianId = useTypedSelector((s) => s.auth.user?.id);

  useEffect(() => {
    invalidateExact('/my-clinician-status');
  }, [invalidateExact]);

  const { coach } = useAuthRoles();
  const api = Api.User.useReadCollectionClients(clinicianId!, params);

  const userClassificationsApi = Api.ClientClassification.useReadCollection();

  if (api.error) return <ScreenError {...api} />;
  if (userClassificationsApi.error)
    return <ScreenError {...userClassificationsApi} />;

  const clients = api.data?.member;
  const total = api.data?.totalItems ?? 0;

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Typography variant="h4" component="h1">
          Your Clients
        </Typography>
        <Stack
          direction="row"
          alignItems="end"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Box minWidth={663}>
            <SearchInput
              label="Search"
              value={params.search || ''}
              onChange={(e) =>
                setModifiedParams({ search: e.target.value, page: '1' })
              }
              placeholder="Search by name"
            />
          </Box>
          <SelectInput
            label="Program Status"
            options={PROGRAM_STAGE_FILTER_OPTIONS}
            labelId="program-status"
            value={params.programStatus || ''}
            onChange={(value) =>
              setModifiedParams({ programStatus: value, page: '1' })
            }
          />
          <SelectInput
            label="User Classification"
            labelId="user-classification"
            value={params['classifications[]'] || ''}
            onChange={(value) =>
              setModifiedParams({ ['classifications[]']: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              ...(userClassificationsApi.data?.member || []).map((uc) => ({
                label: uc.name,
                value: uc.id,
              })),
            ]}
          />
          <SelectInput
            label="Subscription"
            labelId="subscription"
            value={params.activeSubscription || ''}
            onChange={(value) =>
              setModifiedParams({ activeSubscription: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ]}
          />
          {coach && (
            <B2BFilter
              clinicianId={clinicianId!}
              value={params.organisationTeam || ''}
              onChange={(value) =>
                setModifiedParams({ organisationTeam: value, page: '1' })
              }
            />
          )}
        </Stack>

        <Stack spacing={2}>
          <Card>
            {clients ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Program Status</TableCell>
                      <TableCell>Appointments</TableCell>
                      <TableCell>
                        <TableSortLabel
                          {...tableSortLabelProps('registeredAt')}
                        >
                          Registration Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>User Classification</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>B2B</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client: any) => (
                      <ClientsRow key={client['@id']} client={client} />
                    ))}
                  </TableBody>
                  <TableFooter
                    sx={{
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <TableRow>
                      <TablePagination
                        {...tablePaginationProps({
                          totalItems: total,
                          rowsPerPage: 10,
                        })}
                        count={total}
                        colSpan={8}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <SoloLoading />
            )}
          </Card>
        </Stack>
      </Stack>
    </ScreenWrap>
  );
}

function B2BFilter({
  clinicianId,
  value,
  onChange,
}: {
  clinicianId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const api = Api.OrganisationTeam.useReadCollectionUserSub(clinicianId);

  if (api.error) return <ScreenError {...api} />;

  return (
    <SelectInput
      label="B2B"
      labelId="b2b"
      value={value}
      onChange={onChange}
      options={[
        { label: 'All', value: '' },
        ...(api.data?.member || []).map((org) => ({
          label: `${org.organisation.name} - ${org.name}`,
          value: iriToId(org['@id']),
        })),
      ]}
    />
  );
}
